/**
 * InvitationService - Manages invite-only access control
 * 
 * Source of truth: users/{uid}.invited and users/{uid}.role
 * 
 * Roles:
 *   - "admin": Full access + admin features
 *   - "invited": Full access to app
 *   - undefined/null: Not invited, blocked from app
 */

import { doc, getDoc, setDoc, serverTimestamp, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase/init';

/**
 * INVITE_ONLY_MODE - Master toggle for invite-only access
 * 
 * Set via environment variable: VITE_INVITE_ONLY_MODE=false
 * 
 * When false, all authenticated users can access (no invitation check).
 * When true (default), only invited users can access.
 */
export const INVITE_ONLY_MODE = 
  import.meta.env.VITE_INVITE_ONLY_MODE === 'false' ? false : true;

export interface UserInvitation {
  invited: boolean;
  role: 'admin' | 'invited' | null;
  invitedAt?: Timestamp;
  invitedBy?: string;
  revokedAt?: Timestamp;
  revokedBy?: string;
}

export interface InvitationStatus {
  isInvited: boolean;
  isAdmin: boolean;
  role: string | null;
  reason: string;
}

/**
 * Check if a user is invited by looking up their Firestore user doc
 */
export async function checkInvitationStatus(uid: string, _email?: string | null): Promise<InvitationStatus> {
  // If INVITE_ONLY_MODE is disabled, all authenticated users are allowed
  if (!INVITE_ONLY_MODE) {
    console.log('[InvitationService] INVITE_ONLY_MODE=false, allowing all users');
    return { isInvited: true, isAdmin: false, role: 'invited', reason: 'Open access (invite-only disabled)' };
  }

  if (!db) {
    console.warn('[InvitationService] Firestore not initialized');
    return { isInvited: false, isAdmin: false, role: null, reason: 'Database not available' };
  }

  try {
    const userRef = doc(db, 'users', uid);
    const userDoc = await getDoc(userRef);

    if (userDoc.exists()) {
      const data = userDoc.data() as UserInvitation;
      
      if (data.invited === true) {
        const isAdmin = data.role === 'admin';
        return {
          isInvited: true,
          isAdmin,
          role: data.role || 'invited',
          reason: isAdmin ? 'Admin user' : 'Invited user'
        };
      } else {
        return {
          isInvited: false,
          isAdmin: false,
          role: null,
          reason: 'User not invited'
        };
      }
    }

    // No user doc and not a bootstrap admin
    return {
      isInvited: false,
      isAdmin: false,
      role: null,
      reason: 'User not found in invitation list'
    };
  } catch (error) {
    console.error('[InvitationService] Error checking invitation status:', error);
    return {
      isInvited: false,
      isAdmin: false,
      role: null,
      reason: 'Error checking invitation status'
    };
  }
}

/**
 * Invite a user (for use in admin UI or CLI)
 */
export async function inviteUser(
  uid: string,
  email: string,
  role: 'admin' | 'invited' = 'invited',
  invitedBy: string = 'admin'
): Promise<boolean> {
  if (!db) return false;

  try {
    const userRef = doc(db, 'users', uid);
    await setDoc(userRef, {
      email,
      invited: true,
      role,
      invitedAt: serverTimestamp(),
      invitedBy,
      updatedAt: serverTimestamp(),
    }, { merge: true });
    
    console.log(`[InvitationService] User invited: ${email} as ${role}`);
    return true;
  } catch (error) {
    console.error('[InvitationService] Error inviting user:', error);
    return false;
  }
}

/**
 * Revoke a user's invitation
 */
export async function revokeUser(
  uid: string,
  revokedBy: string = 'admin'
): Promise<boolean> {
  if (!db) return false;

  try {
    const userRef = doc(db, 'users', uid);
    await setDoc(userRef, {
      invited: false,
      role: null,
      revokedAt: serverTimestamp(),
      revokedBy,
      updatedAt: serverTimestamp(),
    }, { merge: true });
    
    console.log(`[InvitationService] User revoked: ${uid}`);
    return true;
  } catch (error) {
    console.error('[InvitationService] Error revoking user:', error);
    return false;
  }
}
